import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useCouple } from "./hooks/useCouple";
import { BottomNav } from "./components/BottomNav";
import { TopBar } from "./components/TopBar";
import { LoveNoteOverlay } from "./components/LoveNoteOverlay";
import { PatchNotesOverlay } from "./components/PatchNotesOverlay";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { CompleteProfile } from "./pages/CompleteProfile";
import { Pair } from "./pages/Pair";
import { Home } from "./pages/Home";
import { Calendar } from "./pages/Calendar";
import { Lists } from "./pages/Lists";
import { ListDetail } from "./pages/ListDetail";
import { Links } from "./pages/Links";
import { LinkBoardDetail } from "./pages/LinkBoardDetail";
import { SavingsGoals } from "./pages/SavingsGoals";
import { Todos } from "./pages/Todos";
import { ImportantDates } from "./pages/ImportantDates";
import { Notes } from "./pages/Notes";
import { Mood } from "./pages/Mood";
import { Account } from "./pages/Account";
import { Notifications } from "./pages/Notifications";

function Spinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-ink-400">
      Wird geladen…
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

// Gates the paired-only part of the app. Once no couple document matches
// (freshly registered, or just unpaired), the user is sent to /pair — and
// the reverse happens inside Pair itself, so neither screen can be reached
// in the wrong state. A signed-in user with no profile doc yet (the first
// moment after a brand-new Google sign-in — Google gives no username) is
// sent to /complete-profile first, since Pair itself assumes a profile.
function RequireCouple({ children }: { children: React.ReactNode }) {
  const { user, initializing } = useAuth();
  const { loading, profile, couple } = useCouple();
  const location = useLocation();

  if (initializing || loading) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!profile) return <Navigate to="/complete-profile" replace />;
  if (!couple) return <Navigate to="/pair" replace />;
  return <>{children}</>;
}

function PairRoute() {
  const { initializing } = useAuth();
  const { loading, profile, couple } = useCouple();
  if (initializing || loading) return <Spinner />;
  if (!profile) return <Navigate to="/complete-profile" replace />;
  if (couple) return <Navigate to="/" replace />;
  return <Pair />;
}

function CompleteProfileRoute() {
  const { initializing } = useAuth();
  const { loading, profile } = useCouple();
  if (initializing || loading) return <Spinner />;
  if (profile) return <Navigate to="/pair" replace />;
  return <CompleteProfile />;
}

// A browser normally keeps scroll position across navigations only within
// the same document; this SPA swaps the whole page's content on every route
// change without a real navigation, so without this every tab would open
// wherever the previous tab happened to be scrolled to.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <ChromeGate />
      <LoveNoteOverlay />
      <PatchNotesOverlay />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/complete-profile"
          element={
            <RequireAuth>
              <CompleteProfileRoute />
            </RequireAuth>
          }
        />
        <Route
          path="/pair"
          element={
            <RequireAuth>
              <PairRoute />
            </RequireAuth>
          }
        />
        <Route path="/" element={<RequireCouple><Home /></RequireCouple>} />
        <Route path="/calendar" element={<RequireCouple><Calendar /></RequireCouple>} />
        <Route path="/lists" element={<RequireCouple><Lists /></RequireCouple>} />
        <Route path="/lists/:listId" element={<RequireCouple><ListDetail /></RequireCouple>} />
        <Route path="/links" element={<RequireCouple><Links /></RequireCouple>} />
        <Route path="/links/:boardId" element={<RequireCouple><LinkBoardDetail /></RequireCouple>} />
        <Route path="/shopping" element={<Navigate to="/lists" replace />} />
        <Route path="/savings" element={<RequireCouple><SavingsGoals /></RequireCouple>} />
        <Route path="/todos" element={<RequireCouple><Todos /></RequireCouple>} />
        <Route path="/dates" element={<RequireCouple><ImportantDates /></RequireCouple>} />
        <Route path="/notes" element={<RequireCouple><Notes /></RequireCouple>} />
        <Route path="/mood" element={<RequireCouple><Mood /></RequireCouple>} />
        <Route
          path="/account"
          element={
            <RequireAuth>
              <Account />
            </RequireAuth>
          }
        />
        <Route
          path="/notifications"
          element={
            <RequireAuth>
              <Notifications />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNavGate />
    </>
  );
}

function BottomNavGate() {
  const location = useLocation();
  const hidden = ["/login", "/register", "/complete-profile", "/pair"].includes(location.pathname);
  if (hidden) return null;
  return <BottomNav />;
}

function ChromeGate() {
  const location = useLocation();
  const hidden = ["/login", "/register", "/complete-profile"].includes(location.pathname);
  if (hidden) return null;
  return <TopBar />;
}
